import type { MemberOut } from './types';
import { cn } from '@/shared/lib/cn';
import { useT } from '@/shared/lib/i18n';

interface Props {
  member: MemberOut;
  isCurrentUser?: boolean;
  onClick?: () => void;
}

const CURRENCY_SYMBOL = '₽';

function resolveVariant(member: MemberOut) {
  if (member.status === 'no_bot' || member.status === 'bot_blocked') return 'nobot' as const;
  const b = Number(member.balance);
  if (b === 0) return 'paid' as const;
  if (b < 0) return 'credit' as const;
  return 'debt' as const;
}

const DOT_COLOR = {
  paid:   'bg-[#1f7a3f]',
  debt:   'bg-[#c1262c]',
  nobot:  'bg-[#c1262c]',
  credit: 'bg-[#0c8f80]',
} as const;

const ROW_BG = {
  paid:   'bg-[rgba(244,251,245,0.5)] border border-white/55',
  debt:   'bg-[rgba(244,251,245,0.5)] border border-white/55',
  nobot:  'bg-[rgba(229,42,48,0.11)] border border-[rgba(193,7,16,0.3)]',
  credit: 'bg-[rgba(244,251,245,0.5)] border border-white/55',
} as const;

const AMOUNT_COLOR = {
  paid:   'text-[#1f7a3f]',
  debt:   'text-[#c1262c] font-mono tabular-nums',
  nobot:  'text-[#c1262c] font-mono tabular-nums',
  credit: 'text-[#0c8f80] font-mono tabular-nums',
} as const;

function formatAmount(member: MemberOut, variant: ReturnType<typeof resolveVariant>, t: ReturnType<typeof useT>) {
  const b = Number(member.balance);
  if (variant === 'paid') return t('subscription.memberRow.paid');
  if (variant === 'credit') {
    return t('subscription.memberRow.credit', {
      amount: Math.abs(b).toLocaleString('ru'),
      currency: CURRENCY_SYMBOL,
    });
  }
  return `−${Math.abs(b).toLocaleString('ru')} ${CURRENCY_SYMBOL}`;
}

export function MemberRow({ member, isCurrentUser = false, onClick }: Props) {
  const t = useT();
  const variant = resolveVariant(member);

  return (
    <button
      className={cn(
        'w-full text-left flex items-center gap-3 rounded-[15px] px-[14px] py-3 transition-all active:scale-[0.98]',
        ROW_BG[variant],
      )}
      onClick={onClick}
    >
      {/* Status dot */}
      <span className={cn('w-[9px] h-[9px] rounded-full flex-shrink-0', DOT_COLOR[variant])} />

      {/* Name */}
      <span className="flex-1 text-[15px] font-medium min-w-0 truncate">
        {member.display_name}
        {isCurrentUser && (
          <span className="ml-1.5 text-[11px] text-text-secondary font-normal">{t('subscription.memberRow.holder')}</span>
        )}
        {member.status === 'no_bot' && (
          <span className="ml-1.5 text-[11px] text-status-debt font-semibold">{t('subscription.memberRow.noBot')}</span>
        )}
        {member.status === 'bot_blocked' && (
          <span className="ml-1.5 text-[11px] text-status-debt font-semibold">{t('subscription.memberRow.blocked')}</span>
        )}
        {member.status === 'exit_requested' && (
          <span className="ml-1.5 text-[11px] text-status-warn font-semibold">{t('subscription.memberRow.exit')}</span>
        )}
      </span>

      {/* Amount */}
      <span className={cn('text-[14px] font-semibold', AMOUNT_COLOR[variant])}>
        {formatAmount(member, variant, t)}
      </span>

      {/* Chevron */}
      {onClick && (
        <svg
          width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.4"
          strokeLinecap="round" strokeLinejoin="round"
          className="text-text-muted flex-shrink-0"
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
      )}
    </button>
  );
}
